/**
 * Presence Management Page
 *
 * Main admin page for monitoring and managing real-time user presence across the system.
 * Features real-time WebSocket updates, presence statistics, and user activity tracking.
 */

import { useState } from 'react';
import { IconAlertCircle, IconInfoCircle } from '@tabler/icons-react';
import { Text, Container, Stack, Group, Tabs, Badge, Alert, Title } from '../../../components/ui';
import { FadeIn } from '../../../components/animations';
import { usePresenceManager } from '../../../hooks/usePresenceManager';

// Tab Components
import { OverviewTab } from './components/OverviewTab';
import { UsersTab } from './components/UsersTab';
import { SearchTab } from './components/SearchTab';
import { ConnectionTab } from './components/ConnectionTab';
import { TestingTab } from './components/TestingTab';
import { LiveMonitorTab } from './components/LiveMonitorTab';
import { EventLogTab } from './components/EventLogTab';

export default function PresenceManagementPage() {
  const [activeTab, setActiveTab] = useState<string | null>('overview');
  const { isConnected, isAdmin, presenceStats } = usePresenceManager();

  return (
    <Container size="xl">
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={1}>Presence Management</Title>
              <Text c="dimmed">
                Monitor and manage real-time user presence across the system
              </Text>
            </div>
            <Badge color={isConnected ? 'green' : 'red'} size="lg">
              {isConnected ? 'Connected' : 'Disconnected'}
            </Badge>
          </Group>

          {/* Connection Status Alert */}
          {!isConnected && (
            <Alert color="yellow" title="WebSocket Disconnected" icon={<IconAlertCircle />}>
              The real-time connection to PresenceHub is not active. Some features may not work
              correctly. Check your connection settings or try refreshing the page.
            </Alert>
          )}

          {/* Admin Access Alert */}
          {!isAdmin && !presenceStats && (
            <Alert color="blue" title="Limited Access" icon={<IconInfoCircle />}>
              You do not have admin privileges. Some features like statistics and admin controls
              are not available.
            </Alert>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="users">Users</Tabs.Tab>
              <Tabs.Tab value="search">Search</Tabs.Tab>
              <Tabs.Tab value="connection">Connection</Tabs.Tab>
              <Tabs.Tab value="testing">Testing</Tabs.Tab>
              <Tabs.Tab value="live-monitor">Live Monitor</Tabs.Tab>
              <Tabs.Tab value="event-log">Event Log</Tabs.Tab>
            </Tabs.List>

            {/* Overview Tab */}
            <Tabs.Panel value="overview" pt="lg">
              <OverviewTab />
            </Tabs.Panel>

            {/* Users Tab */}
            <Tabs.Panel value="users" pt="lg">
              <UsersTab />
            </Tabs.Panel>

            {/* Search Tab */}
            <Tabs.Panel value="search" pt="lg">
              <SearchTab />
            </Tabs.Panel>

            {/* Connection Tab */}
            <Tabs.Panel value="connection" pt="lg">
              <ConnectionTab />
            </Tabs.Panel>

            {/* Testing Tab */}
            <Tabs.Panel value="testing" pt="lg">
              <TestingTab />
            </Tabs.Panel>

            {/* Live Monitor Tab */}
            <Tabs.Panel value="live-monitor" pt="lg">
              <LiveMonitorTab />
            </Tabs.Panel>

            {/* Event Log Tab */}
            <Tabs.Panel value="event-log" pt="lg">
              <EventLogTab />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </FadeIn>
    </Container>
  );
}
